import { useMemo } from 'react';
import * as THREE from 'three';
import { DEFAULT_COLORS } from './DefaultMaterial';

const DefaultBoardGrid3D = ({
  rows = 20,
  columns = 10,
  cellSize = 1,
}) => {
  const halfWidth = (columns * cellSize) / 2;
  const height = rows * cellSize;
  const borderThickness = 0.15;

  const gridLines = useMemo(() => {
    const points = [];

    for (let i = 0; i <= columns; i++) {
      const x = i * cellSize - halfWidth;
      points.push(new THREE.Vector3(x, 0, -0.1));
      points.push(new THREE.Vector3(x, height, -0.1));
    }

    for (let i = 0; i <= rows; i++) {
      const y = i * cellSize;
      points.push(new THREE.Vector3(-halfWidth, y, -0.1));
      points.push(new THREE.Vector3(halfWidth, y, -0.1));
    }

    return points;
  }, [rows, columns, cellSize, halfWidth, height]);

  const geometry = useMemo(() => {
    return new THREE.BufferGeometry().setFromPoints(gridLines);
  }, [gridLines]);

  // Plain rectangular border - no rounded corners, no bevel
  const borderShape = useMemo(() => {
    const shape = new THREE.Shape();
    const w = columns * cellSize + borderThickness * 2;
    const h = rows * cellSize + borderThickness * 2;

    shape.moveTo(-w / 2, -h / 2);
    shape.lineTo(w / 2, -h / 2);
    shape.lineTo(w / 2, h / 2);
    shape.lineTo(-w / 2, h / 2);
    shape.lineTo(-w / 2, -h / 2);

    const innerW = columns * cellSize;
    const innerH = rows * cellSize;
    const hole = new THREE.Path();
    hole.moveTo(-innerW / 2, -innerH / 2);
    hole.lineTo(innerW / 2, -innerH / 2);
    hole.lineTo(innerW / 2, innerH / 2);
    hole.lineTo(-innerW / 2, innerH / 2);
    hole.lineTo(-innerW / 2, -innerH / 2);
    shape.holes.push(hole);

    return shape;
  }, [rows, columns, cellSize, borderThickness]);

  return (
    <group>
      {/* Grid lines */}
      <lineSegments geometry={geometry}>
        <lineBasicMaterial
          color={DEFAULT_COLORS.gridLine}
          opacity={0.4}
          transparent
        />
      </lineSegments>

      {/* Back panel */}
      <mesh position={[0, height / 2, -0.2]} receiveShadow>
        <planeGeometry args={[columns * cellSize, rows * cellSize]} />
        <meshStandardMaterial color={DEFAULT_COLORS.background} />
      </mesh>

      {/* Plain flat border - no glow, no bevel */}
      <mesh position={[0, height / 2, 0]}>
        <extrudeGeometry
          args={[
            borderShape,
            {
              depth: 0.1,
              bevelEnabled: false,
            }
          ]}
        />
        <meshStandardMaterial
          color={DEFAULT_COLORS.primary}
          metalness={0}
          roughness={1}
        />
      </mesh>
    </group>
  );
};

export default DefaultBoardGrid3D;
